import { trackingConfig, dlog } from "@/config/trackingConfig";
import { captureAttribution, getAttribution, getFbCookies, isMetaAdVisitor } from "./attribution";
import { installStubs, loadScripts } from "./loaders";

const FIRED_KEY = "tn_fired_events";
const state = { latest: null, history: [], listeners: new Set(), product: null, pageViewFired: false, viewContentFired: false };

export const subscribe = (fn) => { state.listeners.add(fn); return () => state.listeners.delete(fn); };
export const getTrackerState = () => ({ latest: state.latest, history: state.history });
export const newEventId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

const firedSet = () => { try { return new Set(JSON.parse(localStorage.getItem(FIRED_KEY) || "[]")); } catch { return new Set(); } };
const hasFired = (key) => key && firedSet().has(key);
const markFired = (key) => {
  if (!key) return;
  const s = firedSet(); s.add(key);
  try { localStorage.setItem(FIRED_KEY, JSON.stringify([...s].slice(-200))); } catch { /* ignore */ }
};

export function setProductContext(product) {
  state.product = product;
}

export function setTrackingLanguage(lang) {
  state.lang = lang;
}

export function trackLanguageChange(from, to) {
  pushDataLayer({ event: "language_change", from_language: from, page_language: to, page: window.location.pathname, ...getAttribution() });
}

// dataLayer-only engagement events (no Pixel/CAPI), e.g. testimonial_video_play.
export function trackEngagement(event, params = {}) {
  pushDataLayer({ event, page_language: state.lang, page: window.location.pathname, ...productParams(), ...params });
}

const productParams = () => {
  const p = state.product || {};
  return { product_id: p.id, product_name: p.name, product_category: p.category, value: p.price, currency: p.currency || trackingConfig.CURRENCY };
};

const pushDataLayer = (payload) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  dlog("dataLayer", payload.event, payload);
};

const pixelData = (b) => {
  const out = {
    content_ids: b.product_id ? [b.product_id] : undefined, content_name: b.product_name, content_category: b.product_category,
    content_type: "product", value: b.value, currency: b.currency,
  };
  ["cta_name", "cta_position", "contact_method", "call_number_type", "num_items", "order_id", "lead_id", "landing_page", "page_language",
    "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => { if (b[k] !== undefined && b[k] !== "") out[k] = b[k]; });
  Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
  return out;
};

const gaData = (b) => {
  const { event_id, event, ...rest } = b;
  return {
    ...rest, event_id,
    items: b.product_id ? [{ item_id: b.product_id, item_name: b.product_name, item_category: b.product_category, price: state.product?.price, quantity: b.num_items || 1 }] : undefined,
  };
};

const firePixel = (name, data, eventId, custom) => {
  if (!trackingConfig.META_PIXEL_ID || !window.fbq) return;
  window.fbq(custom ? "trackCustom" : "track", name, data, { eventID: eventId });
  dlog("pixel", name, { eventID: eventId, ...data });
};

const fireGA = (name, data) => {
  if (!trackingConfig.GA4_ID || !window.gtag) return;
  window.gtag("event", name, data);
  dlog("ga4", name, data);
};

const sendCapi = (eventName, eventId, customData) => {
  if (!trackingConfig.META_CAPI_ENABLED || !trackingConfig.BACKEND_URL) return;
  const body = { event_name: eventName, event_id: eventId, event_source_url: window.location.href, custom_data: customData, ...getFbCookies() };
  fetch(`${trackingConfig.BACKEND_URL}/api/meta/capi`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), keepalive: true,
  })
    .then((r) => r.json())
    .then((res) => dlog("capi", eventName, eventId, res))
    .catch((e) => dlog("capi", "failed", eventName, e?.message));
};

const record = (entry) => {
  state.latest = entry;
  state.history = [entry, ...state.history].slice(0, 30);
  state.listeners.forEach((fn) => fn(getTrackerState()));
};

// Core emitter: one dataLayer push + optional Pixel / GA4 / CAPI, all sharing one event_id.
function emit({ dl, pixel, pixelCustom = false, ga, capi = false, params = {}, eventId, dedupeKey }) {
  if (hasFired(dedupeKey)) { dlog("dedupe", "skipped duplicate", dedupeKey); return null; }
  const id = eventId || newEventId();
  const base = { ...productParams(), page: window.location.pathname, landing_page: window.location.pathname, page_language: state.lang || "en", ...getAttribution(), ...params, event_id: id };
  pushDataLayer({ event: dl, ...base });
  if (pixel) firePixel(pixel, pixelData(base), id, pixelCustom);
  if (ga) fireGA(ga, gaData(base));
  if (capi && pixel && !pixelCustom) sendCapi(pixel, id, pixelData(base));
  markFired(dedupeKey);
  dlog("event_id", dl, id);
  record({ event: dl, pixel, ga, event_id: id, at: new Date().toISOString(), params: base });
  return id;
}

export function initTracking() {
  installStubs();
  captureAttribution();
  loadScripts();
}

export function trackPageView() {
  if (state.pageViewFired) return null;
  state.pageViewFired = true;
  return emit({ dl: "page_view", pixel: "PageView", ga: "page_view", capi: true, params: { page_title: document.title, page_location: window.location.href } });
}

export function trackViewContent() {
  if (state.viewContentFired || !state.product) return null;
  state.viewContentFired = true;
  return emit({ dl: "view_content", pixel: "ViewContent", ga: "view_item", capi: true });
}

const ctaClick = (cta_name, cta_position, cta_type) =>
  pushDataLayer({ event: "cta_click", cta_name, cta_position, cta_type, page: window.location.pathname, ...productParams(), ...getAttribution() });

let lastContact = { key: "", t: 0 };

export function trackCta(ctaName, position, kind) {
  ctaClick(ctaName, position, kind);
  if (kind === "consult") return emit({ dl: "lead_start", pixel: "LeadStart", pixelCustom: true, ga: "lead_start", params: { cta_name: ctaName, cta_position: position } });
  if (kind === "order") return emit({ dl: "initiate_checkout", pixel: "InitiateCheckout", ga: "begin_checkout", capi: true, params: { cta_name: ctaName, cta_position: position, num_items: 1 } });
  if (kind === "call" || kind === "whatsapp") {
    const now = Date.now();
    if (lastContact.key === ctaName && now - lastContact.t < 1500) return null;
    lastContact = { key: ctaName, t: now };
    const method = kind === "call" ? "phone" : "whatsapp";
    const params = { cta_name: ctaName, cta_position: position, contact_method: method,
      ...(kind === "call" && { call_number_type: trackingConfig.AD_PHONE_NUMBER && isMetaAdVisitor() ? "meta_ad" : "default" }) };
    const id = emit({ dl: "contact_click", pixel: "Contact", ga: "contact", capi: true, params });
    pushDataLayer({ event: kind === "call" ? "call_click" : "whatsapp_click", ...productParams(), ...getAttribution(), ...params, event_id: id });
    return id;
  }
  return null;
}

export function trackLeadFormStart(ctaName, position) {
  return emit({ dl: "lead_start", pixel: "LeadStart", pixelCustom: true, ga: "lead_start", params: { cta_name: ctaName, cta_position: position } });
}

export function trackAddToCart(qty, value) {
  return emit({ dl: "add_to_cart", pixel: "AddToCart", ga: "add_to_cart", capi: true, params: { num_items: qty, value } });
}

// Lead and Purchase: CAPI is sent by the backend with the same event_id, so the browser only fires Pixel/GA4.
export function trackLeadSubmit({ leadId, eventId, ctaName, position }) {
  return emit({ dl: "lead_submit", pixel: "Lead", ga: "generate_lead", eventId, dedupeKey: `lead:${leadId}`, params: { lead_id: leadId, cta_name: ctaName, cta_position: position } });
}

export function trackPurchase({ orderId, eventId, value, qty }) {
  return emit({ dl: "purchase", pixel: "Purchase", ga: "purchase", eventId, dedupeKey: `purchase:${orderId}`, params: { order_id: orderId, transaction_id: orderId, value, num_items: qty } });
}

// Manual triggers for /tracking-test only.
export function trackTestEvent(name) {
  const map = {
    PageView: { dl: "page_view", ga: "page_view" }, ViewContent: { dl: "view_content", ga: "view_item" },
    Lead: { dl: "lead_submit", ga: "generate_lead" }, Contact: { dl: "contact_click", ga: "contact" },
    InitiateCheckout: { dl: "initiate_checkout", ga: "begin_checkout" }, AddToCart: { dl: "add_to_cart", ga: "add_to_cart" },
    Purchase: { dl: "purchase", ga: "purchase" },
  };
  const m = map[name];
  const serverOnly = name === "Lead" || name === "Purchase";
  return emit({ dl: m.dl, pixel: name, ga: m.ga, capi: !serverOnly, params: { cta_name: "tracking_test", cta_position: "tracking_test", test_event: true } });
}
